import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { useSessions, type SessionInfo } from '@/lib/useSessions';
import { deviceLabel, lastSeenLabel } from '@/lib/deviceLabel';

/**
 * Active sessions / devices list for the Privacy settings tab.
 *
 * Shows where the account is currently logged in, marks the current device,
 * lets the user end any other session individually, and offers a single
 * "log out everywhere else" action — confirmed first: it signs every other
 * device out of Cispracs ID too.
 */
export function SessionsList({ onSignOut }: { onSignOut?: () => void } = {}) {
  const { t } = useTranslation();
  const {
    sessions,
    isLoading,
    isError,
    refetch,
    revoke,
    revokingId,
    revokeOthers,
    revokingOthers,
    hasOthers,
  } = useSessions();
  // onSignOut is set only where this device may sign out of Cispracs ID too
  // (PrivacyTab: the web app with OIDC on, not the Desktop shell).
  const canSignOut = onSignOut !== undefined;
  const [confirmingOthers, setConfirmingOthers] = useState(false);

  if (isLoading) {
    return <Note>{t('common.loading')}</Note>;
  }

  if (isError) {
    return (
      <Note>
        {t('prof.sessions.loadFailed')}{' '}
        <button
          type="button"
          onClick={refetch}
          className="font-medium text-accent underline-offset-2 hover:underline"
        >
          {t('prof.sessions.retry')}
        </button>
      </Note>
    );
  }

  if (sessions.length === 0) {
    return <Note>{t('prof.sessions.empty')}</Note>;
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-0.5">
        {sessions.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            revoking={revokingId === s.id}
            onRevoke={() => revoke(s.id)}
          />
        ))}
      </ul>
      {(hasOthers || canSignOut) && (
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {hasOthers && confirmingOthers && (
            <InlineConfirm
              label={t('prof.sessions.logoutOthersConfirm')}
              className="flex flex-wrap items-center justify-end gap-2"
            >
              <span className="text-xs text-danger-ink">{t('prof.sessions.logoutOthersConfirm')}</span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setConfirmingOthers(false);
                  revokeOthers();
                }}
              >
                {t('prof.sessions.logoutOthersYes')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingOthers(false)}>
                {t('prof.sessions.logoutOthersNo')}
              </Button>
            </InlineConfirm>
          )}
          {hasOthers && !confirmingOthers && (
            <Button
              variant="danger"
              size="sm"
              loading={revokingOthers}
              onClick={() => setConfirmingOthers(true)}
            >
              {t('prof.sessions.logoutOthers')}
            </Button>
          )}
          {canSignOut && (
            <Button variant="secondary" size="sm" onClick={onSignOut}>
              {t('prof.sessions.signOut')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  revoking,
  onRevoke,
}: {
  session: SessionInfo;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  // Ending a session signs that device out of Cispracs ID too: ask first.
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/[0.03]">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] text-ink">{deviceLabel(session.uaHint)}</span>
          {session.current && (
            <span className="font-medium shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] text-accent">
              {t('prof.sessions.thisDevice')}
            </span>
          )}
        </div>
        <div className="truncate text-[11px] text-ink-muted">
          {session.current
            ? t('prof.sessions.activeNow')
            : t('prof.sessions.lastSeen', { when: lastSeenLabel(session.lastSeenAt) })}
        </div>
      </div>
      {!session.current && confirming && (
        <InlineConfirm label={t('prof.sessions.endConfirm')} className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-xs text-danger-ink">{t('prof.sessions.endConfirm')}</span>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setConfirming(false);
              onRevoke();
            }}
          >
            {t('prof.sessions.endYes')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            {t('prof.sessions.logoutOthersNo')}
          </Button>
        </InlineConfirm>
      )}
      {!session.current && !confirming && (
        <Button variant="secondary" size="sm" loading={revoking} onClick={() => setConfirming(true)}>
          {t('prof.sessions.end')}
        </Button>
      )}
    </li>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="px-2 py-3 text-[12px] text-ink-muted">{children}</p>;
}
